/**
 * This module handles the authentication bits, which behave a little bit differently
 * than some of the other modules.
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */
import api from '@/api'
import { set as idbSet, del as idbDel } from 'idb-keyval'

import {
	VUEX_AUTH_APP_READY,
	VUEX_AUTH_CRITICAL_INIT_COMPLETE,
	VUEX_AUTH_ERROR,
	VUEX_AUTH_HYDRATE_FROM_TOKEN,
	VUEX_AUTH_REFRESH_FROM_TOKEN,
	VUEX_AUTH_LOGOUT,
	VUEX_AUTH_REQUEST,
	VUEX_AUTH_REQUEST_STATE_ERROR,
	VUEX_AUTH_REQUEST_STATE_LOADING,
	VUEX_AUTH_REQUEST_STATE_SUCCESS,
	VUEX_AUTH_REQUIREMENTS_LOADED,
	VUEX_AUTH_SUCCESS,
	VUEX_AUTH_REFRESH_CURRENT_USER_AVATAR,
    VUEX_AUTH_SWITCH_USER,
} from '@/store/constants/auth'

import {
	VUEX_JOB_CREATE_APPLY_TEMPLATE,
	VUEX_JOB_WATCH_SUCCESS,
	VUEX_JOB_UNWATCH_SUCCESS,
	VUEX_JOB_FLAG_SUCCESS,
	VUEX_JOB_UNFLAG_SUCCESS } from '@/store/constants/jobs'

import { VUEX_NOTIFICATIONS_INIT } from '@/store/constants/notifications'
import { VUEX_ECHO_INIT } from '@/store/constants/echo'
import { VUEX_CONSTANTS_INIT } from '@/store/constants/config/constants'
import { VUEX_PROPERTIES_INIT } from '@/store/constants/config/properties'
import { VUEX_VERSION_INIT } from '@/store/constants/config/version'


/**
 * Auth module state
 * @const
 */
const state = {
	token: null,
	status: null,
	hasLoadedOnce: false,
	currentUser: null,
	authErrors: []
}

/**
 * Auth module getters
 * @const
 */
const getters = {
	isAuthenticated: state => !!state.token,
	authStatus: state => state.status,
	currentUserIsLoaded: state => state.hasLoadedOnce,

	/**
	 * Is the user capable of editing (pushes down into particular domain?)
	 * @function
	 * @todo Get actual prop/role from perms when hooked up
	 */
	getAuthIsEditor() {
		return true
	},

	getRequirementsLoaded: (state) => {
		return state.hasLoadedOnce
	},

    getCurrentUser: (state) => {
        return state.currentUser
    },

    //the debugging tool that lets you switch users - only hill user has rights to switch users
    getSwitchUserPermission: (state) => {
	    if (state.currentUser && state.currentUser.email) {
            return (state.currentUser.email === "hill@thebrandingfarm.com");
        } else {
	        return false
        }
    },

    currentUserHasPerm: (state) => (permissionToCheck) => {
	    if(!state.hasLoadedOnce) return false
        if(!permissionToCheck) return state.currentUser.permissions
        return (state.currentUser.permissions.indexOf(permissionToCheck) > -1)
	},

	currentUserHasRole: (state) => (roleToCheck) => {
	    if(!state.hasLoadedOnce) return false
		if(!roleToCheck) return state.currentUser.roles
        return state.currentUser.roles.indexOf(roleToCheck) > -1
	},

    currentUserHasDepartment: (state) => (permissionToCheck) => {
        if(!state.hasLoadedOnce) return false
        if (state.currentUser.requestor_departments) {
            permissionToCheck = parseInt(permissionToCheck)
            return state.currentUser.requestor_departments.indexOf(permissionToCheck) > -1
        } else {
            return false;
        }
    },


	hasManageUsersPerms: (state, getters) => {
		if(!state.hasLoadedOnce) return false
		return getters.currentUserHasPerm(SILA_PERMISSION_ORGANIZATION_USER_MANAGE_USERS)
	},

	canEditField:(state) => (fieldName) => {
        return state.currentUser.editable_field_list.indexOf(fieldName) > -1
    }
}


/**
 * Auth module action definitions
 * @const
 */
const actions = {
	/**
	 * Performs the api request to authenticate the user
	 * @function
	 * @param {object} context
	 * @param {object} credentials
	 * @todo When the real API is up, revisit this to pass the correct credentials and parse the result accordingly
	 */
	[VUEX_AUTH_REQUEST]: async ({commit, dispatch}, credentials) => {
		commit(VUEX_AUTH_REQUEST)

		await api.post('auth/login', credentials).then(async (auth_response) => {
			let token = auth_response.data.access_token
			await dispatch(VUEX_AUTH_HYDRATE_FROM_TOKEN, token).then(() => {
				commit(VUEX_AUTH_SUCCESS)
			})
		}).catch(async (err) => {
			await idbDel(process.env.VUE_APP_SILA_2_USER_TOKEN).then(async () => {
				// TODO: Push this error to root level Vuex error so we can do something in teh app
				console.error(err)
				commit(VUEX_AUTH_ERROR, err.response.data.error)
			})
		})
	},

    //switch user
    [VUEX_AUTH_SWITCH_USER]: async ({commit, dispatch}, user_id) => {

        await api.post('auth/switch/'+user_id).then(async (auth_response) => {
            let token = auth_response.data.access_token
            await dispatch(VUEX_AUTH_HYDRATE_FROM_TOKEN, token).then(() => {
                commit(VUEX_AUTH_SUCCESS)
                location.reload();
            })

        }).catch(async (err) => {
            console.error('SWITCH USER ERROR',err)
        })
    },


	[VUEX_AUTH_HYDRATE_FROM_TOKEN]: async ({commit, dispatch}, token) => {
		let payload = {}
		payload.token = token
		api.defaults.headers.common['Authorization'] = 'Bearer ' + token

		await idbSet(process.env.VUE_APP_SILA_2_USER_TOKEN, token).then(async () => {
			await api.get('auth/user?include=avatar').then((user_response) => {
				payload.user = user_response.data.data
				commit(VUEX_AUTH_HYDRATE_FROM_TOKEN, payload)
			}).then(async () => {
				await Promise.all([
					commit(VUEX_JOB_CREATE_APPLY_TEMPLATE),
					dispatch(VUEX_ECHO_INIT),
					dispatch(VUEX_CONSTANTS_INIT),
					dispatch(VUEX_PROPERTIES_INIT),
				]).then(() => {
					// TODO: Move non blockers here
                    dispatch(VUEX_AUTH_CRITICAL_INIT_COMPLETE)
					dispatch(VUEX_NOTIFICATIONS_INIT)
					dispatch(VUEX_VERSION_INIT) // coming soon, caching and cache-busting
				})
			})
		})
	},

	[VUEX_AUTH_REFRESH_FROM_TOKEN]:async({commit}) => {
		let payload = {}
		await api.get('auth/user?include=avatar').then((user_response) => {
			payload.user = user_response.data.data
			commit(VUEX_AUTH_HYDRATE_FROM_TOKEN, payload)
		})
	},

    [VUEX_AUTH_CRITICAL_INIT_COMPLETE]: ({rootState, commit, getters}) => {
        //COMMIT
        commit(VUEX_AUTH_CRITICAL_INIT_COMPLETE)


        //SET APPCUES
        //https://studio.appcues.com/settings/installation/guide/properties
        var currentUser = getters.getCurrentUser
        let createdDateUnix = parseInt((new Date(currentUser.created_at).getTime() / 1000).toFixed(0));
        let appcuesObj = {
            name: currentUser.name,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            email: currentUser.email,
            createdAt: createdDateUnix,
            access_type:currentUser.access_type,
            employee_type:currentUser.employee_type,
        }

        for (let i=0;i<rootState.properties.userJobRoles.length;i++) {
            let item = rootState.properties.userJobRoles[i]
            let val = item.value.toString()
            let disp_val = val.split(":").join("_")
            appcuesObj[disp_val] = getters.currentUserHasRole(val)
        }
        for (let i=0;i<rootState.properties.userOrganizationRoles.length;i++) {
            let item = rootState.properties.userOrganizationRoles[i]
            let val = item.value.toString()
            let disp_val = val.split(":").join("_")
            appcuesObj[disp_val] = getters.currentUserHasRole(val)
        }
        for (let i=0;i<rootState.properties.jobDepartments.length;i++) {
            let item = rootState.properties.jobDepartments[i]
            let val = item.value.toString()
            let disp_val = val.split(":").join("_")
            appcuesObj["department_" + disp_val] = getters.currentUserHasDepartment(val)
        }
        window.Appcues.identify(currentUser.id,appcuesObj);
    },


    /**
	 * Performs the api request to authenticate the user (MUST BLOCK RETURN)
	 * @function
	 * @param {object} context
	 */
	[VUEX_AUTH_LOGOUT]: async ({commit}) => {
		await idbDel(process.env.VUE_APP_SILA_2_USER_TOKEN).then(() => {
			api.defaults.headers.common['Authorization'] = null
			commit(VUEX_AUTH_LOGOUT)
		})
	},


}

/**
 * Auth module mutation definitions
 * @const
 */
const mutations = {
	[VUEX_AUTH_REQUEST]: (state) => {
		state.status = VUEX_AUTH_REQUEST_STATE_LOADING
	},

	[VUEX_AUTH_SUCCESS]: (state) => {
		state.status = VUEX_AUTH_REQUEST_STATE_SUCCESS
		state.authErrors = []
	},

	[VUEX_AUTH_ERROR]: (state, payload) => {
		state.status = VUEX_AUTH_REQUEST_STATE_ERROR
		state.authErrors = payload
	},

	[VUEX_AUTH_HYDRATE_FROM_TOKEN]: (state, payload) => {
		state.currentUser = payload.user
		if (payload.token) state.token = payload.token
		state.status = VUEX_AUTH_REQUEST_STATE_SUCCESS
	},

	[VUEX_AUTH_CRITICAL_INIT_COMPLETE]: (state) => {
		state.hasLoadedOnce = true
	},

	[VUEX_AUTH_LOGOUT]: (state) => {
		state.token = null
		state.currentUser = null
		state.status = null
		state.hasLoadedOnce = false
	},

	[VUEX_JOB_WATCH_SUCCESS]: (state, payload) => {
		state.currentUser.watched_jobs.push({ job_id:payload.job_id })
	},

	[VUEX_JOB_UNWATCH_SUCCESS]: (state, payload) => {
		let idx = state.currentUser.watched_jobs.findIndex(x => x.job_id === payload)
		state.currentUser.watched_jobs.splice(idx, 1)
	},

	[VUEX_JOB_FLAG_SUCCESS]: (state, payload) => {
		state.currentUser.flagged_jobs.push({...payload})
	},

	[VUEX_JOB_UNFLAG_SUCCESS]: (state, payload) => {
		let idx = state.currentUser.flagged_jobs.findIndex(x => x.job_id === payload)
		state.currentUser.flagged_jobs.splice(idx, 1)
	},


	// Fired from users after image upload stuff
    // Mutation: Set current user avatar props
    [VUEX_AUTH_REFRESH_CURRENT_USER_AVATAR]: (state, payload) => {
		if (payload.id === state.currentUser.id) {
			state.currentUser.avatar = payload.avatar || null
			state.currentUser.avatar_urls = payload.avatar_urls || {}
		}
    },

}

/**
 * The Auth module of our Vuex store.
 * @module store/modules/auth
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
